/**
 * InteractiveObject — sprite + input + stany + FX; emituje zdarzenia (PLAN-V2 §3.1).
 * Fizyczność bez silnika: drag=lerp+rotacja, drop=mini-odbicie, bounce=parabola+tłumienie.
 */

import Phaser from 'phaser';
import { narrator } from '../core/Narrator.js';
import { catalogText } from '../core/ttsCatalog.js';
import {
  advanceState,
  consumeState,
  createObjectState,
  normalizeDefSize,
  textureKeyForState,
  type ObjectStateSnapshot,
} from './objectState.js';
import { playFx } from './fx.js';
import {
  HIT_AREA_SCALE,
  MIN_OBJECT_SIZE,
  type InteractEvent,
  type InteractListener,
  type InteractionKind,
  type ObjectDef,
} from './types.js';

/**
 * G15 — dobierz klucz TTS / etykietę PL dla ObjectDef (bez pola label w kontrakcie).
 * Preferuj texture (obj_*), potem obj_<id>, potem czytelny tekst z id.
 */
export function resolveObjectNarration(def: ObjectDef): {
  key: string | null;
  text: string;
} {
  const keys: string[] = [];
  if (def.texture) keys.push(def.texture);
  if (def.id.startsWith('obj_')) keys.push(def.id);
  else keys.push(`obj_${def.id}`);
  // cafe_chair_a → obj_cafe_chair
  const baseId = def.id.replace(/_[a-z]$/i, '');
  if (baseId !== def.id) {
    keys.push(baseId.startsWith('obj_') ? baseId : `obj_${baseId}`);
  }

  // Warianty kolorów / liter: house_figure_red → obj_house_figure; school_block_a już wyżej
  const figureBase = def.id.replace(
    /_figure_(red|blue|green|yellow)$/i,
    '_figure',
  );
  if (figureBase !== def.id) {
    keys.push(
      figureBase.startsWith('obj_') ? figureBase : `obj_${figureBase}`,
    );
  }
  if (def.texture) {
    const texBase = def.texture.replace(/_[a-z]$/i, '');
    if (texBase !== def.texture) keys.push(texBase);
    const texFigure = def.texture.replace(
      /_figure_(red|blue|green|yellow)$/i,
      '_figure',
    );
    if (texFigure !== def.texture) keys.push(texFigure);
  }

  for (const k of keys) {
    const t = catalogText(k);
    if (t) return { key: k, text: t };
  }
  // G15: nigdy angielski id — bezpieczny fallback PL (katalog powinien pokryć wszystkie ObjectDef)
  return { key: keys[0] ?? null, text: POLISH_OBJECT_FALLBACK };
}

/** Fallback lektora gdy brak wpisu w katalogu TTS (nie humanizuj angielskich id). */
export const POLISH_OBJECT_FALLBACK = 'Przedmiot';

/** Odtwórz nazwę obiektu (hover = debounce, tap = immediate). */
export function narrateObjectDef(
  def: ObjectDef,
  opts: { immediate?: boolean } = {},
): void {
  const { key, text } = resolveObjectNarration(def);
  if (key && catalogText(key)) {
    narrator.say(key, opts);
  } else {
    narrator.sayText(text, opts);
  }
}

/** Kolory fallback per hash id (czytelne placeholdery). */
const FALLBACK_COLORS = [
  0xff8fab, 0x8fd3ff, 0xffd166, 0xb8f2e6, 0xd4a5ff, 0xffb4a2, 0xa0e8af, 0xfec89a,
];

const FALLBACK_ICONS = ['🍪', '🎾', '🧸', '⭐', '🌸', '🧁', '🦴', '💎', '🔔', '📦'];

export type InteractiveObjectOpts = {
  scene: Phaser.Scene;
  def: ObjectDef;
  x: number;
  y: number;
  /** Unikalne id instancji na warstwie. */
  instanceId: string;
  depth?: number;
};

export class InteractiveObject {
  readonly instanceId: string;
  readonly def: ObjectDef;
  readonly container: Phaser.GameObjects.Container;

  private readonly scene: Phaser.Scene;
  private readonly size: number;
  private stateSnap: ObjectStateSnapshot;
  private visual: Phaser.GameObjects.GameObject;
  private iconText: Phaser.GameObjects.Text | null = null;
  private listeners = new Set<InteractListener>();
  private _dragging = false;
  private _held = false;
  private _enabled = true;

  /** Bounce: prędkość wirtualna. */
  private bounceVx = 0;
  private bounceVy = 0;
  private bouncing = false;
  private readonly gravity = 980;
  private readonly damping = 0.55;
  /** Podłoga — aktualizowana przy drop (WAVE7-E: nie latają w powietrzu). */
  private groundY: number;

  /** Drag lerp. */
  private dragTargetX = 0;
  private dragTargetY = 0;
  private readonly dragLerp = 0.28;

  private homeX: number;
  private homeY: number;

  constructor(opts: InteractiveObjectOpts) {
    this.scene = opts.scene;
    this.def = opts.def;
    this.instanceId = opts.instanceId;
    this.size = normalizeDefSize(opts.def.size, MIN_OBJECT_SIZE);
    this.stateSnap = createObjectState(opts.def);
    this.homeX = opts.x;
    this.homeY = opts.y;
    this.groundY = opts.y;

    this.container = opts.scene.add.container(opts.x, opts.y);
    this.container.setDepth(opts.depth ?? opts.y);
    this.container.setSize(this.size, this.size);
    this.container.setName(`hotspot-object-${opts.def.id}`);

    this.visual = this.buildVisual();
    this.container.add(this.visual);
    if (this.iconText) this.container.add(this.iconText);

    this.setupInput();
  }

  get x(): number {
    return this.container.x;
  }
  get y(): number {
    return this.container.y;
  }
  get state(): ObjectStateSnapshot {
    return this.stateSnap;
  }
  get isDragging(): boolean {
    return this._dragging;
  }
  get isHeld(): boolean {
    return this._held;
  }
  get enabled(): boolean {
    return this._enabled;
  }

  setEnabled(v: boolean): void {
    this._enabled = v;
    if (!v) this.container.disableInteractive();
    else this.enableHitArea();
  }

  on(listener: InteractListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
    this.container.setDepth(y);
  }

  /** Z-sort klucz = y. */
  refreshDepth(): void {
    if (!this._held && !this._dragging) {
      this.container.setDepth(this.container.y);
    }
  }

  private emit(partial: Omit<InteractEvent, 'objectId' | 'defId' | 'x' | 'y'> & {
    x?: number;
    y?: number;
  }): void {
    const evt: InteractEvent = {
      objectId: this.instanceId,
      defId: this.def.id,
      x: partial.x ?? this.container.x,
      y: partial.y ?? this.container.y,
      state: this.stateSnap.state ?? undefined,
      ...partial,
      type: partial.type,
    };
    for (const l of this.listeners) l(evt);
  }

  private colorForDef(): number {
    let h = 0;
    for (let i = 0; i < this.def.id.length; i++) {
      h = (h * 31 + this.def.id.charCodeAt(i)) | 0;
    }
    return FALLBACK_COLORS[Math.abs(h) % FALLBACK_COLORS.length]!;
  }

  private iconForDef(): string {
    let h = 0;
    for (let i = 0; i < this.def.id.length; i++) {
      h = (h * 17 + this.def.id.charCodeAt(i)) | 0;
    }
    return FALLBACK_ICONS[Math.abs(h) % FALLBACK_ICONS.length]!;
  }

  private hasTexture(key: string): boolean {
    return this.scene.textures.exists(key);
  }

  private buildVisual(): Phaser.GameObjects.GameObject {
    const key = textureKeyForState(this.def, this.stateSnap);
    const alt = this.def.texture;
    if (this.hasTexture(key)) {
      const img = this.scene.add.image(0, 0, key);
      img.setDisplaySize(this.size, this.size);
      this.iconText = null;
      return img;
    }
    if (this.hasTexture(alt)) {
      const img = this.scene.add.image(0, 0, alt);
      img.setDisplaySize(this.size, this.size);
      this.iconText = null;
      return img;
    }
    // Fallback: kolorowy kształt + ikona
    const color = this.colorForDef();
    const shape = this.scene.add.rectangle(0, 0, this.size, this.size, color, 1);
    shape.setStrokeStyle(3, 0xffffff, 0.9);
    shape.setOrigin(0.5);
    this.iconText = this.scene.add
      .text(0, 0, this.iconForDef(), { fontSize: `${Math.floor(this.size * 0.45)}px` })
      .setOrigin(0.5);
    return shape;
  }

  private rebuildVisual(): void {
    this.visual.destroy();
    if (this.iconText) {
      this.iconText.destroy();
      this.iconText = null;
    }
    this.visual = this.buildVisual();
    this.container.add(this.visual);
    if (this.iconText) this.container.add(this.iconText);
  }

  private enableHitArea(): void {
    const hit = this.size * HIT_AREA_SCALE;
    this.container.setSize(hit, hit);
    this.container.setInteractive(
      new Phaser.Geom.Rectangle(-hit / 2, -hit / 2, hit, hit),
      Phaser.Geom.Rectangle.Contains,
    );
  }

  private setupInput(): void {
    this.enableHitArea();
    // G15 lektor: hover (debounce w Narrator) + tap/dotyk (immediate, równolegle z akcją)
    this.container.on('pointerover', () => {
      if (!this._enabled) return;
      narrateObjectDef(this.def);
    });
    this.container.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this._enabled || this._held) return;
      narrateObjectDef(this.def, { immediate: true });
      const kinds = this.def.interactions;
      const canDrag = kinds.includes('drag') || kinds.includes('pickup');
      const canBounce = kinds.includes('bounce');

      // Bounce bez drag — od razu kopnięcie
      if (canBounce && !canDrag) {
        this.kickBounce(pointer);
        return;
      }

      // Krótki tap vs drag: dopiero po progu ruchu zaczynamy przeciąganie;
      // tap bez ruchu → intencja (podejście psa / akcja) przez ObjectLayer.
      const startX = pointer.worldX;
      const startY = pointer.worldY;
      let dragging = false;
      const DRAG_THRESHOLD = 12;

      const onMove = (p: Phaser.Input.Pointer) => {
        if (!canDrag) return;
        const dist = Math.hypot(p.worldX - startX, p.worldY - startY);
        if (!dragging && dist >= DRAG_THRESHOLD) {
          dragging = true;
          this.beginDrag(p);
        }
        if (dragging && this._dragging) {
          this.dragTargetX = p.worldX;
          this.dragTargetY = p.worldY;
          this.emit({
            type: 'drag-move',
            kind: 'drag',
            x: this.container.x,
            y: this.container.y,
          });
        }
      };

      const onUp = (p: Phaser.Input.Pointer) => {
        this.scene.input.off('pointermove', onMove);
        this.scene.input.off('pointerup', onUp);
        if (dragging && this._dragging) {
          this.endDrag(p);
          return;
        }
        // tap (bez drag) — bounce z drag też może dostać tap-intencję
        if (canBounce && !canDrag) {
          this.kickBounce(p);
          return;
        }
        this.emit({ type: 'tap', kind: 'tap' });
      };

      this.scene.input.on('pointermove', onMove);
      this.scene.input.on('pointerup', onUp);
    });
  }

  private beginDrag(pointer: Phaser.Input.Pointer): void {
    this._dragging = true;
    this.dragTargetX = pointer.worldX;
    this.dragTargetY = pointer.worldY;
    this.container.setDepth(10_000);
    this.emit({
      type: 'drag-start',
      kind: this.def.interactions.includes('pickup') ? 'pickup' : 'drag',
    });
  }

  private endDrag(pointer: Phaser.Input.Pointer): void {
    if (!this._dragging) return;
    this._dragging = false;
    this.container.setRotation(0);
    // WAVE7-E: podłoga = miejsce upuszczenia (przedmioty nie wiszą w powietrzu)
    this.groundY = this.container.y;
    this.emit({
      type: 'drag-end',
      kind: 'drag',
      x: pointer.worldX,
      y: pointer.worldY,
    });
    this.playDropBounce();
  }

  /** Delikatne „siadanie” po upuszczeniu — bez latania w powietrzu. */
  playDropBounce(): void {
    const baseY = this.groundY;
    this.container.y = baseY;
    this.scene.tweens.add({
      targets: this.container,
      y: baseY - 8,
      duration: 70,
      yoyo: true,
      ease: 'Quad.Out',
      onComplete: () => {
        this.container.y = baseY;
        this.refreshDepth();
      },
    });
    this.scene.tweens.add({
      targets: this.container,
      scaleX: 1.05,
      scaleY: 0.95,
      duration: 70,
      yoyo: true,
    });
  }

  private kickBounce(pointer: Phaser.Input.Pointer): void {
    const dx = this.container.x - pointer.worldX;
    const dy = this.container.y - pointer.worldY;
    const len = Math.hypot(dx, dy) || 1;
    // krótszy, niższy podskok — Toca-feel
    this.bounceVx = (dx / len) * 180 + (Math.random() - 0.5) * 20;
    this.bounceVy = -180 - Math.random() * 40;
    this.bouncing = true;
    this.emit({ type: 'bounce', kind: 'bounce' });
    if (this.def.fx) {
      playFx(this.scene, this.def.fx, this.container.x, this.container.y);
    }
  }

  /** Pickup: obiekt „w ręce" / za wskaźnikiem (warstwa steruje). */
  markHeld(held: boolean): void {
    this._held = held;
    if (held) {
      this.container.setDepth(10_001);
      this.emit({ type: 'pickup', kind: 'pickup' });
    } else {
      this.emit({ type: 'drop', kind: 'pickup' });
      this.groundY = this.container.y;
      this.playDropBounce();
      this.refreshDepth();
    }
  }

  /** Toggle stanu + FX. */
  doToggle(): ObjectStateSnapshot {
    this.stateSnap = advanceState(this.stateSnap, this.def);
    this.rebuildVisual();
    this.emit({ type: 'toggle', kind: 'toggle', state: this.stateSnap.state ?? undefined });
    if (this.def.fx) {
      playFx(this.scene, this.def.fx, this.container.x, this.container.y);
    }
    return this.stateSnap;
  }

  /** Consume (jedzenie itp.). */
  doConsume(): ObjectStateSnapshot {
    this.stateSnap = consumeState(this.stateSnap, this.def);
    this.rebuildVisual();
    this.emit({ type: 'consume', kind: 'consume', state: this.stateSnap.state ?? undefined });
    if (this.def.fx) {
      playFx(this.scene, this.def.fx, this.container.x, this.container.y);
    } else {
      playFx(this.scene, 'crumbs', this.container.x, this.container.y);
    }
    // lekki zniknięcie gdy fully consumed bez stanów
    if (this.stateSnap.consumed && !(this.def.states?.length)) {
      this.scene.tweens.add({
        targets: this.container,
        alpha: 0.35,
        scale: 0.85,
        duration: 200,
      });
    }
    return this.stateSnap;
  }

  hasKind(kind: InteractionKind): boolean {
    return this.def.interactions.includes(kind);
  }

  /** Pętla: drag lerp + bounce parabola. Wywołuj z ObjectLayer.update. */
  update(dtSec: number): void {
    if (this._dragging) {
      const lx = Phaser.Math.Linear(this.container.x, this.dragTargetX, this.dragLerp);
      const ly = Phaser.Math.Linear(this.container.y, this.dragTargetY, this.dragLerp);
      this.container.setPosition(lx, ly);
      // lekka rotacja w kierunku ruchu
      const dx = this.dragTargetX - lx;
      this.container.setRotation(Phaser.Math.Clamp(dx * 0.004, -0.35, 0.35));
      return;
    }

    if (this.bouncing) {
      this.bounceVy += this.gravity * dtSec;
      this.container.x += this.bounceVx * dtSec;
      this.container.y += this.bounceVy * dtSec;
      // „podłoga" = homeY lub bieżący ground
      const floor = this.groundY;
      if (this.container.y >= floor && this.bounceVy > 0) {
        this.container.y = floor;
        this.bounceVy = -this.bounceVy * this.damping;
        this.bounceVx *= this.damping;
        if (Math.abs(this.bounceVy) < 40 && Math.abs(this.bounceVx) < 30) {
          this.bouncing = false;
          this.bounceVx = 0;
          this.bounceVy = 0;
          this.container.y = floor;
        }
      }
      this.refreshDepth();
    }
  }

  destroy(): void {
    this.listeners.clear();
    this.container.destroy(true);
  }
}
